import { TwoFactorPendingResponse } from '../../../api/twoFactor/types';

import Button from '../../components/Button';
import Input from '../../components/Input';
import Text from '../../components/Text';
import { t } from '../../i18n/langue';
import CodeParEmail from './CodeParEmail';
import CodesDeSecours from './CodesDeSecours';
import ConfigurationApplication from './ConfigurationApplication';
import { useSecondFacteur } from './useSecondFacteur';

/**
 * Double authentification dans la PWA — même règle que le web.
 *
 * Sans cet écran l'app serait une porte dérobée : le serveur ne délivre plus de
 * jeton à un compte 2FA sur le seul mot de passe, et l'ancien écran aurait lu un
 * `token` absent.
 */

interface SecondFacteurProps {
    attente: TwoFactorPendingResponse;
    email: string;
    onConnecte: (token: string) => void;
    /** Retour au mot de passe, avec un message si le challenge a expiré. */
    onRetour: (message: string | null) => void;
}

export default function SecondFacteur({ attente, email, onConnecte, onRetour }: SecondFacteurProps) {
    const {
        etape,
        estEnrolement,
        estEmail,
        configPrete,
        setConfigPrete,
        code,
        setCode,
        codeDeSecours,
        confiance,
        setConfiance,
        erreur,
        enCours,
        sessionEnAttente,
        modeEmail,
        indiceEnrolement,
        renvoiEnCours,
        soumettre,
        renvoyer,
        utiliserEmail,
        basculerCodeDeSecours,
        revenirALApplication,
        traiterErreur,
    } = useSecondFacteur({ attente, email, onConnecte, onRetour });

    if (sessionEnAttente) {
        return (
            <CodesDeSecours
                codes={sessionEnAttente.recovery_codes ?? []}
                onContinuer={() => onConnecte(sessionEnAttente.token)}
            />
        );
    }

    return (
        <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
                e.preventDefault();
                void soumettre();
            }}
        >
            <Text variante="titre" as="h1">
                {estEnrolement ? t('connexion.securisezCompte') : t('connexion.verificationDeuxEtapes')}
            </Text>

            {estEnrolement ? (
                modeEmail ? (
                    <>
                        {indiceEnrolement !== null ? (
                            <CodeParEmail
                                indice={indiceEnrolement}
                                envoye
                                renvoiEnCours={renvoiEnCours}
                                onRenvoyer={(redemarrer) => void renvoyer(redemarrer)}
                            />
                        ) : erreur ? (
                            // Envoi initial en échec : la mise en place a déjà noté l'erreur
                            // (bloc plus bas) ; on propose seulement de réessayer, sans monter
                            // `CodeParEmail` avant qu'un code soit réellement parti (sinon
                            // « n'a pas pu être envoyé » s'affiche pendant la requête, et le
                            // compte à rebours de 60 s ne redémarre pas quand elle aboutit).
                            <Button libelle={t('commun.reessayer')} variante="secondaire" onPress={() => void utiliserEmail()} pleineLargeur />
                        ) : (
                            <div className="h-32 animate-pulse rounded-xl bg-main-bg-color" />
                        )}
                        <Button libelle={t('connexion.utiliserApplication')} variante="discret" onPress={revenirALApplication} />
                    </>
                ) : (
                    <>
                        <Text variante="petit" ton="attenue" className="block leading-snug">
                            {t('connexion.installezApplication')}
                        </Text>
                        <ConfigurationApplication
                            challengeId={etape.challenge_id}
                            onPrete={() => setConfigPrete(true)}
                            onErreur={traiterErreur}
                            onUtiliserEmail={etape.email_allowed ? () => void utiliserEmail() : undefined}
                        />
                    </>
                )
            ) : estEmail ? (
                <CodeParEmail
                    indice={etape.email_hint ?? ''}
                    envoye={etape.email_sent !== false}
                    renvoiEnCours={renvoiEnCours}
                    onRenvoyer={(redemarrer) => void renvoyer(redemarrer)}
                />
            ) : (
                <Text variante="petit" ton="attenue" className="block leading-snug">
                    {codeDeSecours
                        ? t('connexion.saisissezCodeDeSecours')
                        : t('connexion.saisissezCodeApplication')}
                </Text>
            )}

            {/* `key` : remonté à chaque bascule, pour que l'autoFocus rejoue. */}
            <Input
                key={codeDeSecours ? 'secours' : 'code'}
                libelle={codeDeSecours ? t('connexion.codeDeSecours') : t('connexion.codeDeVerification')}
                valeur={code}
                onChange={(v) => setCode(codeDeSecours ? v : v.replace(/\D/g, '').slice(0, 6))}
                inputMode={codeDeSecours ? 'text' : 'numeric'}
                autoComplete={codeDeSecours ? 'off' : 'one-time-code'}
                disabled={enCours || (estEnrolement && (modeEmail ? indiceEnrolement === null : !configPrete))}
                autoFocus
            />

            <label className="flex min-h-touch items-center gap-3 text-sm text-text-color">
                <input type="checkbox" checked={confiance} onChange={(e) => setConfiance(e.target.checked)} className="h-5 w-5" />
                {t('connexion.faireConfiance')}
            </label>

            {erreur && (
                <div role="alert">
                    <Text variante="petit" ton="danger" className="block leading-snug">{erreur}</Text>
                </div>
            )}

            <Button
                libelle={enCours ? t('connexion.verificationEnCours') : estEnrolement ? t('connexion.activerEtConnecter') : t('connexion.verifier')}
                type="submit"
                disabled={enCours || !code || (!codeDeSecours && code.length !== 6)}
                pleineLargeur
            />

            {!estEnrolement && !estEmail && (
                <Button
                    libelle={codeDeSecours ? t('connexion.utiliserCodeApplication') : t('connexion.utiliserCodeDeSecours')}
                    variante="lien"
                    onPress={basculerCodeDeSecours}
                />
            )}

            <Button libelle={t('commun.retour')} variante="discret" onPress={() => onRetour(null)} />
        </form>
    );
}
